import { z } from 'zod';
import { EVENT_TYPES, EventTypeSchema } from './events';

export const WEBHOOK_SECRET_MIN = 16;
export const WEBHOOK_SECRET_MAX = 128;
export const WEBHOOK_URL_MAX = 2048;

/** Deliveries only ever go to TLS endpoints. */
export const WebhookUrlSchema = z
  .url({ protocol: /^https$/, error: 'webhook url must use https' })
  .max(WEBHOOK_URL_MAX);

const EventTypesSchema = z
  .array(EventTypeSchema)
  .min(1)
  .max(EVENT_TYPES.length)
  .superRefine((types, ctx) => {
    const seen = new Set<string>();
    types.forEach((type, i) => {
      if (seen.has(type)) {
        ctx.addIssue({ code: 'custom', message: `duplicate event type ${type}`, path: [i] });
      }
      seen.add(type);
    });
  });

const SecretSchema = z.string().min(WEBHOOK_SECRET_MIN).max(WEBHOOK_SECRET_MAX);

/** `POST /admin/v1/webhooks` (S-05-15); dispatch arrives in Bolt 8. */
export const CreateWebhookSchema = z.strictObject({
  name: z.string().trim().min(1).max(80),
  url: WebhookUrlSchema,
  eventTypes: EventTypesSchema,
  /** HMAC signing secret; returned masked afterwards. */
  secret: SecretSchema,
  enabled: z.boolean().default(true),
});
export type CreateWebhook = z.infer<typeof CreateWebhookSchema>;
export type CreateWebhookInput = z.input<typeof CreateWebhookSchema>;

export const UpdateWebhookSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(80),
    url: WebhookUrlSchema,
    eventTypes: EventTypesSchema,
    secret: SecretSchema,
    enabled: z.boolean(),
  })
  .partial();
export type UpdateWebhook = z.infer<typeof UpdateWebhookSchema>;

/** Keeps the last four characters so an admin can tell secrets apart. */
export const maskSecret = (secret: string): string => `${'*'.repeat(8)}${secret.slice(-4)}`;

export const WebhookSubscriptionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  url: z.string(),
  eventTypes: z.array(EventTypeSchema),
  /** Masked (`maskSecret`); the full value is never returned. */
  secret: z.string(),
  enabled: z.boolean(),
  /** Dead deliveries in a row; disabled automatically at `WEBHOOK_MAX_CONSECUTIVE_FAILURES`. */
  consecutiveFailures: z.int().nonnegative(),
  /** Why the platform disabled the subscription (ADR-0019 §6); null while healthy or
   *  admin-disabled by hand. */
  disabledReason: z.string().nullable(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type WebhookSubscription = z.infer<typeof WebhookSubscriptionSchema>;
